import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { CheckCircle2, Download, FileUp, TriangleAlert } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FormError } from '@/components/form';
import { EmptyState, ErrorState, PageHeader, Section } from '@/components/page';
import { StatusPill } from '@/features/register/badges';
import { api, ApiError, errorMessage } from '@/lib/api';
import { can, meQuery, useMe } from '@/lib/auth';
import { formatDateTime } from '@/lib/labels';
import type { ImportBatch, Page } from '@/lib/types';

export const Route = createFileRoute('/_app/imports')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me || !can.admin(me)) throw redirect({ to: '/entries', search: {} });
  },
  component: ImportsPage,
});

function ImportsPage() {
  const me = useMe();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<ImportBatch | null>(null);

  const history = useQuery({ queryKey: ['imports'], queryFn: () => api.get<Page<ImportBatch>>('/v1/imports', { pageSize: 20 }) });

  const run = useMutation({
    mutationFn: async (dryRun: boolean) => {
      const form = new FormData();
      form.append('file', file!);
      try {
        return await api.upload<ImportBatch>('/v1/imports', form, { dryRun });
      } catch (err) {
        // A rejected import still returns its report.
        if (err instanceof ApiError && err.status === 422 && Array.isArray(err.body.rows)) return err.body as unknown as ImportBatch;
        throw err;
      }
    },
    onSuccess: (batch) => {
      setReport(batch);
      void queryClient.invalidateQueries({ queryKey: ['imports'] });
      if (batch.status === 'COMPLETED') {
        toast.success(`Imported ${batch.casesCreated} historical case(s)`);
        void queryClient.invalidateQueries({ queryKey: ['entries'] });
        void queryClient.invalidateQueries({ queryKey: ['cases'] });
        setFile(null);
        if (fileRef.current) fileRef.current.value = '';
      }
    },
  });

  const problems = report?.rows?.filter((r) => r.errors.length || r.skipped.length) ?? [];

  return (
    <>
      <PageHeader
        title="Backfill imports"
        description="Load historical fraud records from a spreadsheet. Check the file first; an import only happens if every row is valid."
        actions={
          <Button variant="outline" onClick={() => api.download('/v1/imports/template', 'fraud-safe-backfill-template.csv').catch((e) => toast.error(errorMessage(e)))}>
            <Download /> Download CSV template
          </Button>
        }
      />
      <div className="grid gap-5">
        <Section title="Upload a file" description="CSV in the template’s format. Several values in one cell are separated by “;”.">
          <div className="grid gap-4 p-5">
            <FormError message={run.isError ? errorMessage(run.error) : null} />
            <div className="flex flex-wrap items-center gap-3">
              <label htmlFor="import-file" className="sr-only">
                CSV file
              </label>
              <Input
                id="import-file"
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="max-w-sm"
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  setReport(null);
                  run.reset();
                }}
              />
              <Button variant="outline" disabled={!file || run.isPending} onClick={() => run.mutate(true)}>
                <FileUp /> {run.isPending && run.variables ? 'Checking…' : 'Check file'}
              </Button>
              {report?.status === 'VALIDATED' ? (
                <Button disabled={run.isPending || !can.write(me)} onClick={() => run.mutate(false)}>
                  {run.isPending && run.variables === false ? 'Importing…' : `Import ${report.validRows} case(s)`}
                </Button>
              ) : null}
            </div>
            {!can.write(me) ? <p className="text-sm text-muted-foreground">Choose a client (top right) before importing.</p> : null}

            {report ? (
              <div className="grid gap-3">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  {report.status === 'REJECTED' ? (
                    <TriangleAlert className="size-5 text-status-danger-fg" aria-hidden="true" />
                  ) : (
                    <CheckCircle2 className="size-5 text-status-active-fg" aria-hidden="true" />
                  )}
                  <span className="font-medium">
                    {report.status === 'COMPLETED'
                      ? `Imported ${report.casesCreated} case(s) from ${report.filename}.`
                      : report.status === 'VALIDATED'
                        ? `All ${report.totalRows} row(s) are valid. Nothing has been imported yet.`
                        : `${report.invalidRows} of ${report.totalRows} row(s) need fixing. Nothing was imported.`}
                  </span>
                </div>
                {problems.length ? (
                  <div className="overflow-hidden rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted hover:bg-muted">
                          <TableHead className="w-20 px-4">Line</TableHead>
                          <TableHead>Reference</TableHead>
                          <TableHead>Problems</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {problems.map((r) => (
                          <TableRow key={r.line}>
                            <TableCell className="px-4 tabular-nums">{r.line}</TableCell>
                            <TableCell className="font-mono text-[13px]">{r.sourceReference ?? '—'}</TableCell>
                            <TableCell>
                              <ul className="grid gap-0.5 text-sm">
                                {r.errors.map((e, i) => (
                                  <li key={`e${i}`} className="text-status-danger-fg">
                                    {e}
                                  </li>
                                ))}
                                {r.skipped.map((s, i) => (
                                  <li key={`s${i}`} className="text-muted-foreground">
                                    Not stored: {s.column}: {s.reason}
                                  </li>
                                ))}
                              </ul>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </Section>

        <Section title="Previous imports">
          {history.isError ? (
            <div className="p-4">
              <ErrorState error={history.error} />
            </div>
          ) : history.data && history.data.items.length === 0 ? (
            <div className="p-4">
              <EmptyState title="No imports yet" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="px-5">File</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead className="text-right">Rows</TableHead>
                  <TableHead className="text-right">Cases</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(history.data?.items ?? []).map((b) => (
                  <TableRow key={b.id}>
                    <TableCell className="px-5">{b.filename}</TableCell>
                    <TableCell>
                      {b.status === 'COMPLETED' ? (
                        <StatusPill tone="active">Imported</StatusPill>
                      ) : b.status === 'VALIDATED' ? (
                        <StatusPill tone="removed">Checked only</StatusPill>
                      ) : (
                        <StatusPill tone="danger">Rejected</StatusPill>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{b.totalRows}</TableCell>
                    <TableCell className="text-right tabular-nums">{b.casesCreated}</TableCell>
                    <TableCell className="text-muted-foreground">{b.createdByName ?? '—'}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{formatDateTime(b.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      </div>
    </>
  );
}
